<template>
    <div>
        <login-header class="stack-xl" />
        <div class="container">
            <template v-if="thanksMessage">
                <div class="login">
                    <div class="tile stack-m">
                        <div class="tile-title">
                            <img class="image image-128 image-center"
                                 src="/static/icons/blank-state/email-blank-state-icon.svg"/>
                        </div>
                        <div class="tile-content text-center">
                            <h2 class="stack-m">Thank you for signing up!</h2>
                            <p>A confirmation link has been sent to your email address. You must confirm your account
                                before you are able to sign in.</p>
                        </div>
                    </div>
                    <div class="tile tile-secondary stack-l">
                        <div class="tile-content">
                            <p class="text-center">Go back to
                                <router-link :to="{name: 'login'}">Log In</router-link>
                            </p>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <v-error-messages
                    :has-frontend-errors="$v.form.$error"
                    :server-error="serverError"
                    class="stack-l">
                    <li class="list-item" v-if="!$v.form.company.required">Company is required</li>
                    <li class="list-item" v-if="!$v.form.firstName.required">First Name is required</li>
                    <li class="list-item" v-if="!$v.form.lastName.required">Last Name is required</li>
                    <li class="list-item" v-if="!$v.form.businessPhone.required">Phone number is required</li>
                    <li class="list-item" v-if="!$v.form.email.required">Email is required</li>
                    <li class="list-item" v-if="!$v.form.email.email">Email is invalid</li>
                    <li class="list-item" v-if="!$v.form.website.required">Website is required</li>
                    <li class="list-item" v-if="!$v.form.website.url">Website is invalid</li>
                    <li class="list-item" v-if="!$v.form.password.required">Password is required</li>
                    <li class="list-item" v-if="!$v.form.password.minLength">Password must be at least 8 characters
                        long.
                    </li>
                    <li class="list-item" v-if="!$v.form.password.mustContainOneNumber">Password must contain at least 1
                        digit.
                    </li>
                    <li class="list-item" v-if="!$v.form.password.mustContainLowerCase">Password must contain at least 1
                        lowercase letter.
                    </li>
                </v-error-messages>
                <div class="grid">
                    <div class="grid-item grid-item-half">
                        <div class="tile tile-description">
                            <div class="tile-title">
                                <h1>Create your Rebilly account today.</h1>
                            </div>
                            <div class="tile-content">
                                <p class="stack-m">No credit card required.</p>
                                <p class="stack-l">Rebilly powers subscription billing for thousands of websites. Join
                                    our customers who are using Rebilly to maximize their customer lifetime value.</p>
                                <div class="testimonial">
                                    <div class="testimonial-quote testimonial-quote-bubble stack-l">
                                        “Rebilly's dunning process has significantly reduced my customer churn and
                                        allowed me to focus on finding new customers rather than having to deal with
                                        failing credit cards.”
                                    </div>
                                    <div class="testimonial-author">
                                        <img class="testimonial-author-image" src="/static/images/thunderclap.png">
                                        <div class="testimonial-author-info">
                                            <strong>Dillon Newman</strong>
                                            <h3 class="text-muted">Co-Founder at Thunderclap Media Group</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="grid-item grid-item-secondary">
                        <div class="tile stack-m position-relative">
                            <v-inline-loader v-if="isLoading"/>
                            <div class="tile-content">
                                <div class="grid-content">
                                    <div class="grid-item grid-item-full">
                                        <r-input
                                            auto-focus
                                            label="Company Name"
                                            v-model="form.company"
                                            :validate="$v.form.company"
                                            @key-submit="save(form)"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-full">
                                        <r-input
                                            label="First Name"
                                            v-model="form.firstName"
                                            :validate="$v.form.firstName"
                                            @key-submit="save(form)"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-full">
                                        <r-input
                                            label="Last Name"
                                            v-model="form.lastName"
                                            :validate="$v.form.lastName"
                                            @key-submit="save(form)"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-full">
                                        <r-input
                                            label="Phone Number"
                                            v-model="form.businessPhone"
                                            :validate="$v.form.businessPhone"
                                            @key-submit="save(form)"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-full">
                                        <r-input
                                            label="Email Address"
                                            v-model="form.email"
                                            :validate="$v.form.email"
                                            @key-submit="save(form)"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-full">
                                        <r-input
                                            label="Website"
                                            v-model="form.website"
                                            :validate="$v.form.website"
                                            help-text="e.g. https://www.mycompany.com"
                                            @key-submit="save(form)"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-full">
                                        <v-select
                                            label="Country"
                                            :options="countryList"
                                            placeholder="Select Country"
                                            @input="changeCurrency"
                                            v-model="form.country"
                                            @key-submit="save(form)"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-full">
                                        <v-select
                                            label="Reporting Currency"
                                            :preselect-first="true"
                                            :options="currenciesList"
                                            v-model="form.reportingCurrency"
                                            @key-submit="save(form)"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-full">
                                        <r-input
                                            class="stack-m"
                                            label="Password"
                                            v-model="form.password"
                                            :password="true"
                                            left-icon="lock"
                                            :validate="$v.form.password"
                                            help-text="Password must be at least 8 characters long and contain at least 1 lowercase letter and 1 digit."
                                            @key-submit="save(form)"
                                        />
                                    </div>
                                    <div class="grid-item grid-item-full">
                                        <p class="text-muted text-center stack-s">
                                            By creating your account, you agree to Rebilly's <a
                                            href="https://www.rebilly.com/api-license/" target="_blank">License</a> and
                                            <a
                                                href="https://www.rebilly.com/terms-of-use/"
                                                target="_blank">Terms Of Use</a>. <br/>
                                            To learn about how we use your information, read our <a
                                            href="https://www.rebilly.com/privacy-policy/"
                                            target="_blank">Privacy Notice</a>.
                                        </p>
                                        <r-button
                                            type="primary"
                                            class="stack-m"
                                            @click="save(form)"
                                            :fluid="true"
                                            :loading="isLoading">
                                            Create Account
                                        </r-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="tile tile-secondary stack-l">
                            <div class="tile-content">
                                <p class="text-center">Already have an account?
                                    <router-link :to="{name: 'login'}">Log In Here</router-link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
    import LoginHeader from '@/modules/guest/login-header';
    import {
        vErrorMessages, vSelect,
    } from '@/components/form';
    import vInlineLoader from '@/components/v-inline-loader';
    import {COUNTRIES_LIST} from '@/data/countries';
    import {CURRENCIES_LIST} from '@/data/currencies';
    import {getCurrencyCode} from '@/data/currencies-countries';
    import {
        email, minLength, required, url,
    } from 'vuelidate/lib/validators';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';

    export default {
        components: {
            LoginHeader, vErrorMessages, vSelect, vInlineLoader,
        },
        mixins: [FormLoaderMixin],
        data() {
            return {
                isLoading: false,
                serverError: null,
                countryList: COUNTRIES_LIST,
                currenciesList: CURRENCIES_LIST,
                thanksMessage: false,
                form: {
                    email: null,
                    company: null,
                    firstName: null,
                    lastName: null,
                    businessPhone: null,
                    password: null,
                    website: 'https://',
                    country: 'US',
                    reportingCurrency: 'USD',
                },
            };
        },
        validations: {
            form: {
                email: {required, email},
                company: {required},
                firstName: {required},
                lastName: {required},
                businessPhone: {required},
                website: {required, url},
                password: {
                    required,
                    minLength: minLength(8),
                    mustContainOneNumber(pwd) {
                        return /\d/.test(pwd);
                    },
                    mustContainLowerCase(pwd) {
                        return /[a-z]/.test(pwd);
                    },
                },
            },
        },
        methods: {
            changeCurrency(countryCode) {
                this.form.reportingCurrency = getCurrencyCode(countryCode);
            },
            async getLocation() {
                try {
                    this.isLoading = true;
                    const {fields} = await this.$api(({location}) => location.query());
                    if (fields.country !== null) {
                        this.form.country = fields.country;
                        this.form.reportingCurrency = fields.currency;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this.isLoading = false;
                }
            },
            async create(data) {
                try {
                    await this.$api(({account}) => account.signUp({data}));
                    this.thanksMessage = true;
                    this.triggerGTMEvent({
                        event: 'signup',
                        category: 'signup',
                        action: 'signup',
                    });
                } catch (error) {
                    this.$toast.fromError(error, 'Unable to process your account. Please contact support.');
                    throw error;
                }
            },
        },
        created() {
            this.getLocation();
        },
    };
</script>
